import { CONNECTOR_AUTH_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Field, Icon, Input, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import type { WebhookOut } from "../../shared";
import { authBodyOf, authHeaderProblem, authValueProblem } from "./connector-auth-model";
import { Gate, useCanManage } from "./panel-kit";
import { useConnectorAuthChange } from "./use-connector-auth";

const A = "settings.webhooks.connectorAuth";

function AuthDialog({ webhook, current, onClose }: { webhook: WebhookOut; current: WebhookOut["connector_auth"]; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const change = useConnectorAuthChange(webhook.id);
  const [header, setHeader] = useState(current?.header ?? "");
  const [value, setValue] = useState("");
  const [tried, setTried] = useState(false);
  const headerProblem = authHeaderProblem(header);
  const valueProblem = authValueProblem(value);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    setTried(true);
    if (headerProblem || valueProblem) return;
    change.mutate(authBodyOf(header, value), {
      onSuccess: () => { toast.success(t(`${A}.saved`)); onClose(); },
      onError: (caught) => { toast.error(message(caught)); },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t(current ? `${A}.replaceTitle` : `${A}.setTitle`)} description={t(`${A}.dialogBody`)} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <Field label={t(`${A}.header`)} hint={t(`${A}.headerHint`)} error={tried && headerProblem ? t(`${A}.headerProblems.${headerProblem}`) : undefined}>
            <Input className="font-mono text-xs" autoComplete="off" spellCheck={false} maxLength={CONNECTOR_AUTH_LIMITS.header} placeholder={t(`${A}.headerPlaceholder`)} value={header} onChange={(event) => { setHeader(event.target.value); }} />
          </Field>
          <Field label={t(`${A}.value`)} hint={t(`${A}.valueHint`)} error={tried && valueProblem ? t(`${A}.valueProblems.${valueProblem}`, { max: CONNECTOR_AUTH_LIMITS.value }) : undefined}>
            <Input type="password" className="font-mono text-xs" autoComplete="new-password" spellCheck={false} maxLength={CONNECTOR_AUTH_LIMITS.value} value={value} onChange={(event) => { setValue(event.target.value); }} />
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" loading={change.isPending}>{t(`${A}.save`)}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RemoveDialog({ webhook, onClose }: { webhook: WebhookOut; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const change = useConnectorAuthChange(webhook.id);
  const remove = () => {
    change.mutate(null, {
      onSuccess: () => { toast.success(t(`${A}.removed`)); onClose(); },
      onError: (caught) => { toast.error(message(caught)); },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${A}.removeTitle`)}
        description={t(`${A}.removeBody`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={change.isPending} onClick={remove}>{t(`${A}.remove`)}</Button>
          </>
        }
      >
        <p className="font-mono text-xs text-fg-muted">{webhook.url}</p>
      </DialogContent>
    </Dialog>
  );
}

type Open = "set" | "remove" | null;

/**
 * Connector authentication of an endpoint that workflows call: a header name and a secret value the Data connector sends
 * with every request. Write-only: the secret is never shown again, only the header name and the last four characters.
 */
export function ConnectorAuthSection({ webhook }: { webhook: WebhookOut }) {
  const { t } = useTranslation();
  const manage = useCanManage("webhooks.manage");
  const [open, setOpen] = useState<Open>(null);
  const auth = webhook.connector_auth;
  return (
    <div className="mt-3 rounded-md bg-surface-2 p-3" data-testid="connector-auth">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="lock" className="size-4 shrink-0 text-fg-muted" />
        <h3 className="text-sm font-semibold">{t(`${A}.title`)}</h3>
        {auth ? <Badge className="font-mono" data-testid="connector-auth-badge">{`${auth.header} ••••${auth.last4}`}</Badge> : <span className="text-xs text-fg-muted">{t(`${A}.notSet`)}</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Gate reason={manage.reason}>
            <Button variant="secondary" size="sm" disabled={!manage.allowed} onClick={() => { setOpen("set"); }}>
              <Icon name={auth ? "rotate-ccw" : "plus"} />
              {t(auth ? `${A}.replace` : `${A}.set`)}
            </Button>
          </Gate>
          {auth ? (
            <Button variant="ghost" size="sm" className="text-danger" disabled={!manage.allowed} onClick={() => { setOpen("remove"); }}>
              <Icon name="trash" />
              {t(`${A}.remove`)}
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-xs text-fg-muted">{t(`${A}.hint`)}</p>
      {open === "set" ? <AuthDialog webhook={webhook} current={auth} onClose={() => { setOpen(null); }} /> : null}
      {open === "remove" ? <RemoveDialog webhook={webhook} onClose={() => { setOpen(null); }} /> : null}
    </div>
  );
}
